import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Heart, Shield, Phone, UserPlus, Trash2, Save, ArrowLeft,
  Stethoscope, AlertTriangle, CheckCircle, X
} from 'lucide-react';
import { ChildIcon, genderColor } from '../components/ChildIcon';
import { cn } from '@/lib/utils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Field = ({ label, name, value, onChange, placeholder, type = 'text', icon: Icon, hint }) => (
  <div className="space-y-1.5">
    <Label htmlFor={name} className="text-sm font-medium flex items-center gap-1.5" style={{ color: 'var(--text-primary)' }}>
      {Icon && <Icon className="h-3.5 w-3.5" style={{ color: 'var(--primary-300)' }} />}
      {label}
    </Label>
    <Input
      id={name} name={name} type={type}
      value={value || ''} onChange={onChange}
      placeholder={placeholder} className="h-10"
      data-testid={`health-field-${name}`}
    />
    {hint && <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>{hint}</p>}
  </div>
);

export default function ChildHealth() {
  const { contractId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [contract, setContract] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  const [healthData, setHealthData] = useState({
    child_doctor_name: '',
    child_doctor_phone: '',
    child_ss_number: '',
    child_allergies: '',
    child_medical_treatments: '',
    child_special_diet: '',
    child_vaccinations_uptodate: true,
  });

  const [authData, setAuthData] = useState({
    authorized_pickups: [],
    emergency_contacts: [],
    medical_care_authorized: true,
    outings_authorized: true,
  });

  const [newPickup, setNewPickup] = useState('');
  const [newContact, setNewContact] = useState({ name: '', phone: '', relation: '' });

  const fetchContract = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/contracts/${contractId}`);
      const c = res.data;
      setContract(c);
      setHealthData({
        child_doctor_name: c.child_doctor_name || '',
        child_doctor_phone: c.child_doctor_phone || '',
        child_ss_number: c.child_ss_number || '',
        child_allergies: c.child_allergies || '',
        child_medical_treatments: c.child_medical_treatments || '',
        child_special_diet: c.child_special_diet || '',
        child_vaccinations_uptodate: c.child_vaccinations_uptodate !== false,
      });
      setAuthData({
        authorized_pickups: c.authorized_pickups || [],
        emergency_contacts: c.emergency_contacts || [],
        medical_care_authorized: c.medical_care_authorized !== false,
        outings_authorized: c.outings_authorized !== false,
      });
      setHasChanges(false);
    } catch (err) {
      toast.error('Erreur lors du chargement du contrat');
      navigate('/contrats');
    } finally {
      setLoading(false);
    }
  }, [contractId, navigate]);

  useEffect(() => { fetchContract(); }, [fetchContract]);

  const handleHealthChange = (e) => {
    setHealthData(prev => ({ ...prev, [e.target.name]: e.target.value }));
    setHasChanges(true);
  };

  const addPickup = () => {
    if (!newPickup.trim()) return;
    setAuthData(prev => ({ ...prev, authorized_pickups: [...prev.authorized_pickups, newPickup.trim()] }));
    setNewPickup('');
    setHasChanges(true);
  };

  const removePickup = (idx) => {
    setAuthData(prev => ({
      ...prev,
      authorized_pickups: prev.authorized_pickups.filter((_, i) => i !== idx)
    }));
    setHasChanges(true);
  };

  const addContact = () => {
    if (!newContact.name.trim() || !newContact.phone.trim()) return;
    setAuthData(prev => ({
      ...prev,
      emergency_contacts: [...prev.emergency_contacts, { ...newContact }]
    }));
    setNewContact({ name: '', phone: '', relation: '' });
    setHasChanges(true);
  };

  const removeContact = (idx) => {
    setAuthData(prev => ({
      ...prev,
      emergency_contacts: prev.emergency_contacts.filter((_, i) => i !== idx)
    }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const payload = {
        ...healthData,
        child_vaccinations_uptodate: healthData.child_vaccinations_uptodate,
        authorized_pickups: authData.authorized_pickups,
        emergency_contacts: authData.emergency_contacts,
        medical_care_authorized: authData.medical_care_authorized,
        outings_authorized: authData.outings_authorized,
      };

      // Remove empty strings
      Object.keys(payload).forEach(k => {
        if (payload[k] === '') payload[k] = null;
      });

      await axios.put(`${API}/contracts/${contractId}`, payload);
      setHasChanges(false);
      toast.success('Informations mises à jour');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="container-custom py-6 space-y-4 animate-fade-in">
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-[400px] w-full rounded-xl" />
      </div>
    );
  }

  const childName = contract?.child_first_name || contract?.child_name || 'Enfant';
  const childSex = contract?.child_sex || null;
  const isParent = user?.role === 'parent';

  return (
    <div className="container-custom py-6 space-y-6 animate-fade-in" data-testid="child-health-page">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate('/contrats')} data-testid="back-to-contracts">
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-xl font-bold flex items-center gap-2" style={{ fontFamily: 'var(--font-secondary)', color: 'var(--text-primary)' }}>
            <ChildIcon sex={childSex} className="h-5 w-5" style={{ color: genderColor(childSex) }} />
            {childName}
          </h1>
          <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
            Santé, autorisations & contacts d'urgence
          </p>
        </div>
      </div>

      <Tabs defaultValue="health" className="w-full">
        <TabsList className="w-full grid grid-cols-2 h-auto p-1" style={{ backgroundColor: 'var(--neutral-100)' }}>
          <TabsTrigger
            value="health"
            className="text-sm py-2 data-[state=active]:bg-[var(--bg-primary)] data-[state=active]:text-[var(--primary-400)] data-[state=active]:shadow-sm"
            data-testid="tab-health"
          >
            <Heart className="h-4 w-4 mr-1.5" />
            Santé
          </TabsTrigger>
          <TabsTrigger
            value="authorizations"
            className="text-sm py-2 data-[state=active]:bg-[var(--bg-primary)] data-[state=active]:text-[var(--primary-400)] data-[state=active]:shadow-sm"
            data-testid="tab-authorizations"
          >
            <Shield className="h-4 w-4 mr-1.5" />
            Autorisations
          </TabsTrigger>
        </TabsList>

        {/* ── Tab: Santé ── */}
        <TabsContent value="health" className="mt-4 space-y-4">
          <Card style={{ borderColor: 'var(--neutral-200)' }}>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
                <Stethoscope className="h-4 w-4" style={{ color: 'var(--primary-400)' }} />
                Médecin traitant
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field
                  label="Nom du médecin" name="child_doctor_name"
                  value={healthData.child_doctor_name} onChange={handleHealthChange}
                  placeholder="Dr Martin" icon={Stethoscope}
                />
                <Field
                  label="Téléphone" name="child_doctor_phone"
                  value={healthData.child_doctor_phone} onChange={handleHealthChange}
                  placeholder="01 23 45 67 89" type="tel" icon={Phone}
                />
              </div>
              <Field
                label="N° Sécurité sociale (rattaché)" name="child_ss_number"
                value={healthData.child_ss_number} onChange={handleHealthChange}
                placeholder="1 85 12 75 108 234 56" icon={Shield}
                hint="Numéro du parent auquel l'enfant est rattaché"
              />
            </CardContent>
          </Card>

          <Card style={{ borderColor: 'var(--neutral-200)' }}>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
                <Heart className="h-4 w-4" style={{ color: '#E17A73' }} />
                Informations médicales
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="child_allergies" className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                  Allergies
                </Label>
                <Textarea
                  id="child_allergies" name="child_allergies"
                  value={healthData.child_allergies} onChange={handleHealthChange}
                  placeholder="Aucune allergie connue, ou détaillez..."
                  rows={2} data-testid="health-field-allergies"
                />
              </div>
              <div>
                <Label htmlFor="child_medical_treatments" className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                  Traitements médicaux en cours
                </Label>
                <Textarea
                  id="child_medical_treatments" name="child_medical_treatments"
                  value={healthData.child_medical_treatments} onChange={handleHealthChange}
                  placeholder="Aucun traitement, ou détaillez..."
                  rows={2} data-testid="health-field-treatments"
                />
              </div>
              <div>
                <Label htmlFor="child_special_diet" className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                  Régime alimentaire spécifique
                </Label>
                <Textarea
                  id="child_special_diet" name="child_special_diet"
                  value={healthData.child_special_diet} onChange={handleHealthChange}
                  placeholder="Aucun régime particulier, ou détaillez..."
                  rows={2} data-testid="health-field-diet"
                />
              </div>

              <div className="flex items-center justify-between p-3 rounded-lg border border-[var(--neutral-200)] bg-[var(--neutral-50)]">
                <div>
                  <Label htmlFor="vaccinations" className="font-semibold text-sm cursor-pointer">
                    Vaccinations à jour
                  </Label>
                  <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                    DTP obligatoire pour l'accueil en collectivité
                  </p>
                </div>
                <Switch
                  id="vaccinations"
                  checked={healthData.child_vaccinations_uptodate}
                  onCheckedChange={(checked) => {
                    setHealthData(prev => ({ ...prev, child_vaccinations_uptodate: checked }));
                    setHasChanges(true);
                  }}
                  data-testid="health-vaccinations-switch"
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── Tab: Autorisations ── */}
        <TabsContent value="authorizations" className="mt-4 space-y-4">
          {/* Toggles */}
          <Card style={{ borderColor: 'var(--neutral-200)' }}>
            <CardHeader className="pb-3">
              <CardTitle className="text-base" style={{ color: 'var(--text-primary)' }}>
                Autorisations parentales
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center justify-between p-3 rounded-lg border border-[var(--neutral-200)]">
                <div>
                  <Label className="font-semibold text-sm cursor-pointer">Soins médicaux d'urgence</Label>
                  <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                    Autoriser l'assistante maternelle à faire soigner l'enfant
                  </p>
                </div>
                <Switch
                  checked={authData.medical_care_authorized}
                  onCheckedChange={(checked) => {
                    setAuthData(prev => ({ ...prev, medical_care_authorized: checked }));
                    setHasChanges(true);
                  }}
                  data-testid="auth-medical-switch"
                />
              </div>
              <div className="flex items-center justify-between p-3 rounded-lg border border-[var(--neutral-200)]">
                <div>
                  <Label className="font-semibold text-sm cursor-pointer">Sorties extérieures</Label>
                  <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                    Autoriser les promenades, sorties au parc, etc.
                  </p>
                </div>
                <Switch
                  checked={authData.outings_authorized}
                  onCheckedChange={(checked) => {
                    setAuthData(prev => ({ ...prev, outings_authorized: checked }));
                    setHasChanges(true);
                  }}
                  data-testid="auth-outings-switch"
                />
              </div>
            </CardContent>
          </Card>

          {/* Authorized Pickups */}
          <Card style={{ borderColor: 'var(--neutral-200)' }}>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
                <UserPlus className="h-4 w-4" style={{ color: 'var(--primary-400)' }} />
                Personnes autorisées à récupérer l'enfant
              </CardTitle>
              <CardDescription>En plus des parents</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {authData.authorized_pickups.map((person, idx) => (
                <div key={`pickup-${person}-${idx}`} className="flex items-center justify-between p-2.5 rounded-lg border border-[var(--neutral-200)] bg-[var(--neutral-50)]">
                  <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>{person}</span>
                  {isParent && (
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-[var(--error)] hover:text-[var(--error)] hover:bg-[var(--error-light)]"
                      onClick={() => removePickup(idx)} data-testid={`remove-pickup-${idx}`}>
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              ))}
              {isParent && (
                <div className="flex gap-2">
                  <Input
                    value={newPickup} onChange={(e) => setNewPickup(e.target.value)}
                    placeholder="Nom complet de la personne"
                    className="flex-1 h-9"
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addPickup())}
                    data-testid="new-pickup-input"
                  />
                  <Button variant="outline" size="sm" onClick={addPickup} className="shrink-0"
                    data-testid="add-pickup-btn">
                    <UserPlus className="h-4 w-4" />
                  </Button>
                </div>
              )}
              {authData.authorized_pickups.length === 0 && (
                <p className="text-sm italic" style={{ color: 'var(--text-tertiary)' }}>
                  Aucune personne ajoutée
                </p>
              )}
            </CardContent>
          </Card>

          {/* Emergency Contacts */}
          <Card style={{ borderColor: 'var(--neutral-200)' }}>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
                <AlertTriangle className="h-4 w-4" style={{ color: '#E17A73' }} />
                Contacts d'urgence
              </CardTitle>
              <CardDescription>Personnes à contacter en cas de problème</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {authData.emergency_contacts.map((contact, idx) => (
                <div key={`contact-${contact.name}-${idx}`} className="flex items-center justify-between p-3 rounded-lg border border-[var(--neutral-200)] bg-[var(--neutral-50)]">
                  <div>
                    <p className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>{contact.name}</p>
                    <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                      {contact.phone}{contact.relation ? ` — ${contact.relation}` : ''}
                    </p>
                  </div>
                  {isParent && (
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-[var(--error)] hover:text-[var(--error)] hover:bg-[var(--error-light)]"
                      onClick={() => removeContact(idx)} data-testid={`remove-contact-${idx}`}>
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              ))}
              {isParent && (
                <div className="space-y-2 p-3 rounded-lg border border-dashed border-[var(--neutral-300)]">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                    <Input
                      value={newContact.name}
                      onChange={(e) => setNewContact(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="Nom" className="h-9"
                      data-testid="new-contact-name"
                    />
                    <Input
                      value={newContact.phone}
                      onChange={(e) => setNewContact(prev => ({ ...prev, phone: e.target.value }))}
                      placeholder="Téléphone" type="tel" className="h-9"
                      data-testid="new-contact-phone"
                    />
                    <div className="flex gap-2">
                      <Input
                        value={newContact.relation}
                        onChange={(e) => setNewContact(prev => ({ ...prev, relation: e.target.value }))}
                        placeholder="Lien (ex: grand-mère)" className="h-9 flex-1"
                        data-testid="new-contact-relation"
                      />
                      <Button variant="outline" size="sm" onClick={addContact} className="shrink-0 h-9"
                        data-testid="add-contact-btn">
                        <UserPlus className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              )}
              {authData.emergency_contacts.length === 0 && (
                <p className="text-sm italic" style={{ color: 'var(--text-tertiary)' }}>
                  Aucun contact d'urgence ajouté
                </p>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Save Button */}
      {isParent && (
        <div className="sticky bottom-20 md:bottom-4 z-40 flex justify-end" data-testid="health-save-area">
          <Button
            onClick={handleSave}
            disabled={!hasChanges || saving}
            className={cn(
              "shadow-lg transition-all duration-200 gap-2",
              hasChanges
                ? "bg-[var(--primary-400)] hover:bg-[var(--primary-500)] text-white"
                : "bg-[var(--neutral-200)] text-[var(--text-secondary)] cursor-not-allowed"
            )}
            size="lg"
            data-testid="health-save-button"
          >
            <Save className="h-4 w-4" />
            {saving ? 'Enregistrement...' : 'Enregistrer'}
          </Button>
        </div>
      )}
    </div>
  );
}
